// processSelection — the pure, DOM-free selection model for the process TREE
// surface (P4 #28). A Set of walker `NodeId`s plus a click ANCHOR, driven by a
// small reducer so every gesture (plain / ⌘-toggle / Shift-range / clear) is
// unit-testable without React. `useProcessSelection` wraps it for the view.
//
// Selection is VIEW STATE ONLY — it never mutates the model; the toolbar reads
// the selected nodeIds and funnels each through the shared ProcessStepEditApi.
// Range extension needs a stable, ordered list of the currently-rendered step
// nodeIds (document order); the caller derives it from the walk (`orderedStepIds`
// below) and passes it into every range action so the reducer stays pure.

import { useCallback, useMemo, useReducer } from "react";
import type { NodeId, WalkNode, WalkResult } from "@archlang/engine";

export interface SelectionState {
  /** The selected step nodeIds. Order-independent (a Set); document order for
   *  range math comes from the caller's `order` list. */
  readonly selected: ReadonlySet<NodeId>;
  /** The last row a plain/⌘ click landed on — the pivot a Shift-range extends
   *  from. Null until the first click. */
  readonly anchor: NodeId | null;
}

export const EMPTY_SELECTION: SelectionState = { selected: new Set(), anchor: null };

export type SelectionAction =
  /** Plain click — select ONLY this row (the single-select 1-item case), and
   *  make it the new anchor. */
  | { readonly type: "replace"; readonly id: NodeId }
  /** ⌘/Ctrl click — flip this row in/out of the set; it becomes the anchor. */
  | { readonly type: "toggle"; readonly id: NodeId }
  /** Shift click — select the inclusive run from the anchor to `id` (in the
   *  given document order). With no anchor it degrades to a plain replace. */
  | { readonly type: "range"; readonly id: NodeId; readonly order: readonly NodeId[] }
  /** Drop the whole selection (Esc / click-away). */
  | { readonly type: "clear" };

/** The inclusive slice of `order` between two ids (either direction). Empty when
 *  either id is absent from `order` (a defensive no-op, never a throw). */
export function rangeBetween(order: readonly NodeId[], a: NodeId, b: NodeId): NodeId[] {
  const i = order.indexOf(a);
  const j = order.indexOf(b);
  if (i === -1 || j === -1) return [];
  const [lo, hi] = i <= j ? [i, j] : [j, i];
  return order.slice(lo, hi + 1);
}

export function selectionReducer(state: SelectionState, action: SelectionAction): SelectionState {
  switch (action.type) {
    case "replace":
      return { selected: new Set([action.id]), anchor: action.id };
    case "toggle": {
      const next = new Set(state.selected);
      if (next.has(action.id)) next.delete(action.id);
      else next.add(action.id);
      // The anchor follows the clicked row even when toggled OFF, so a
      // following Shift-click ranges from where the pointer last was.
      return { selected: next, anchor: action.id };
    }
    case "range": {
      const pivot = state.anchor ?? action.id;
      const run = rangeBetween(action.order, pivot, action.id);
      if (run.length === 0) return { selected: new Set([action.id]), anchor: action.id };
      // Keep the anchor put — repeated Shift-clicks re-extend from the same pivot
      // (native list-selection behaviour), never walking the anchor along.
      return { selected: new Set(run), anchor: pivot };
    }
    case "clear":
      return EMPTY_SELECTION;
  }
}

export interface ProcessSelection {
  readonly selected: ReadonlySet<NodeId>;
  readonly anchor: NodeId | null;
  readonly count: number;
  readonly has: (id: NodeId) => boolean;
  /** Resolve a click into the right action given its modifier keys. Plain →
   *  replace, ⌘/Ctrl → toggle, Shift → range (needs the doc order). */
  readonly onRowClick: (id: NodeId, mods: ClickModifiers, order: readonly NodeId[]) => void;
  readonly replace: (id: NodeId) => void;
  readonly toggle: (id: NodeId) => void;
  readonly range: (id: NodeId, order: readonly NodeId[]) => void;
  readonly clear: () => void;
}

export interface ClickModifiers {
  /** ⌘ (mac) or Ctrl (win/linux) — toggle. */
  readonly meta: boolean;
  /** Shift — range. */
  readonly shift: boolean;
}

/** Read the modifier bits off a mouse/keyboard event (⌘ OR Ctrl = toggle). */
export function clickModifiers(e: {
  readonly metaKey: boolean;
  readonly ctrlKey: boolean;
  readonly shiftKey: boolean;
}): ClickModifiers {
  return { meta: e.metaKey || e.ctrlKey, shift: e.shiftKey };
}

export function useProcessSelection(): ProcessSelection {
  const [state, dispatch] = useReducer(selectionReducer, EMPTY_SELECTION);
  const replace = useCallback((id: NodeId) => dispatch({ type: "replace", id }), []);
  const toggle = useCallback((id: NodeId) => dispatch({ type: "toggle", id }), []);
  const range = useCallback(
    (id: NodeId, order: readonly NodeId[]) => dispatch({ type: "range", id, order }),
    [],
  );
  const clear = useCallback(() => dispatch({ type: "clear" }), []);
  const onRowClick = useCallback(
    (id: NodeId, mods: ClickModifiers, order: readonly NodeId[]) => {
      // Shift wins over ⌘ (native list behaviour: Shift+⌘ still ranges).
      if (mods.shift) dispatch({ type: "range", id, order });
      else if (mods.meta) dispatch({ type: "toggle", id });
      else dispatch({ type: "replace", id });
    },
    [],
  );
  return useMemo(
    () => ({
      selected: state.selected,
      anchor: state.anchor,
      count: state.selected.size,
      has: (id: NodeId) => state.selected.has(id),
      onRowClick,
      replace,
      toggle,
      range,
      clear,
    }),
    [state, onRowClick, replace, toggle, range, clear],
  );
}

/** Document-order list of every STEP nodeId in a walk tree — the order a
 *  Shift-range walks. Only leaf steps are selectable rows; container heads
 *  (if/each/do/…) are excluded (they carry their own edit menu, not a row
 *  selection). Mirrors the tree's own depth-first render order. */
export function orderedStepIds(walk: WalkResult | null): NodeId[] {
  if (!walk) return [];
  const out: NodeId[] = [];
  const visit = (nodes: readonly WalkNode[]): void => {
    for (const n of nodes) {
      switch (n.kind) {
        case "step":
          out.push(n.nodeId);
          break;
        case "each":
          visit(n.children);
          break;
        case "parallel":
          for (const b of n.branches) visit(b.children);
          break;
        case "if":
          for (const b of n.branches) visit(b.children);
          if (n.elseBranch) visit(n.elseBranch.children);
          break;
        case "switch":
          for (const b of n.cases) visit(b.children);
          break;
        case "try":
          visit(n.tryBranch.children);
          for (const b of n.catches) visit(b.children);
          break;
        case "do":
          if (n.body) visit(n.body);
          break;
        case "terminator":
          break;
      }
    }
  };
  visit(walk.tree);
  return out;
}
